/** @jsxImportSource @emotion/react */
import { PlusOutlined } from "@ant-design/icons";
import { css } from "@emotion/react";
import { Button, Form, Input, Table, Tag } from "antd";
import _ from "lodash";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import UserCreate from "src/containers/aiPlatform/user/components/UserCreate";
import { useFormatMessage } from "src/hooks";
import messageIds from "src/locales/messageIds";
import { UserList } from "src/models/aiPlatform";
import { AiServiceClient } from "src/services/AIPlatformService";

export default React.memo(function UserManagement() {
  const formatMessage = useFormatMessage();
  const [loading, setLoading] = useState(false);
  const [isModalShow, setIsModalShow] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [dataSource, setDataSource] = useState([] as UserList[]);

  const columns = useMemo(
    () => [
      {
        title: formatMessage(messageIds.aiManage.userManagment.ssoIdentityUid),
        dataIndex: "ssoIdentityUid",
        ellipsis: true,
        render: (text: string, record: UserList) => (
          <span>{record?.userInfo?.ssoIdentityUid}</span>
        ),
      },
      {
        title: formatMessage(messageIds.common.name),
        dataIndex: "name",
        ellipsis: true,
        render: (text: string, record: UserList) => (
          <span>{record?.userInfo?.name}</span>
        ),
      },
      {
        title: formatMessage(messageIds.common.createTime),
        dataIndex: "createDatetime ",
        // width: "15%",
        width: 200,
        render: (text: string, record: UserList) =>
          moment(record?.userInfo?.createDatetime).format(
            "YYYY-MM-DD HH:mm:ss"
          ),
      },
      {
        title: formatMessage(messageIds.aiManage.userManagment.isSaas),
        dataIndex: "isSaas",
        width: 80,
        ellipsis: true,
        render: (text: boolean, record: UserList) =>
          record?.userInfo?.isSaas === undefined ? "无" : record?.userInfo?.isSaas === false ? "是" : "否",
      },
      {
        title: formatMessage(messageIds.aiManage.userManagment.status),
        dataIndex: "status",
        width: 100,
        ellipsis: true,
        render: (text: number, record: UserList) =>
          record?.userInfo?.status === 1 ? (
            <span
              css={css`
                display: flex;
                align-items: center;
              `}
            >
              <div
                css={css`
                  border-radius: 100%;
                  background-color: #00dd00;
                  width: 8px;
                  height: 8px;
                  margin-right: 7px;
                `}
              />
              {formatMessage(messageIds.aiManage.userManagment.enable)}
            </span>
          ) : (
            <span
              css={css`
                display: flex;
                align-items: center;
              `}
            >
              <div
                css={css`
                  border-radius: 100%;
                  background-color: red;
                  width: 8px;
                  height: 8px;
                  margin-right: 7px;
                `}
              />
              {formatMessage(messageIds.aiManage.userManagment.disable)}
            </span>
          ),
      },
      {
        title: formatMessage(messageIds.aiManage.userManagment.accountType),
        dataIndex: "accountType",
        ellipsis: true,
        width: 100,
        render: (text: number, record: UserList) =>
          record?.userInfo?.accountType === 0 ? (
            <Tag
              color="green"
              css={css`
                font-size: 14px;
              `}
            >
              {formatMessage(messageIds.aiManage.userManagment.prepaid)}
            </Tag>
          ) : (
            <Tag
              color="blue"
              css={css`
                font-size: 14px;
              `}
            >
              {formatMessage(messageIds.aiManage.userManagment.noprepaid)}
            </Tag>
          ),
      },
    ],
    [formatMessage]
  );

  const fetch = useCallback((name?: string, email?: string, phone?: string) => {
    setLoading(true);
    AiServiceClient.allUserList(
      _.trim(name),
      _.trim(email),
      _.trim(phone)
    ).then((res) => {
      setDataSource(res.data.data);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const onCreate = useCallback(() => {
    setIsModalShow(!isModalShow);
  }, [isModalShow]);

  const onSearch = useCallback(() => {
    fetch(name, email, phone);
  }, [fetch, name, email, phone]);

  return (
    <div
      css={css`
        flex-grow: 1;
        padding: 30px;
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          margin-right: 30px;
          padding-bottom: 30px;
        `}
      >
        <div
          css={css`
            max-width: 1000px;
            margin-right: 20px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          `}
        >
          <h1
            css={css`
              position: relative;
              margin: 0px 0px 8px;
              padding-left: 10px;
              line-height: 18px;
              font-size: 18px;
              font-weight: 600;
              color: rgb(61, 73, 102);
              ::before {
                content: "";
                position: absolute;
                left: 0px;
                width: 3px;
                height: 18px;
                background: rgb(51, 119, 255);
              }
            `}
          >
            {formatMessage(messageIds.menu.aiManagement.user.name)}
          </h1>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          css={css`
            padding: 4px 20px;
            border-radius: 4;
            background-color: rgb(51, 119, 255);
          `}
          onClick={onCreate}
        >
          {formatMessage(messageIds.common.create)}
        </Button>
      </div>
      <div
        css={css`
          display: flex;
          padding-bottom: 24px;
          border-bottom: 1px solid rgb(202, 209, 224);
        `}
      >
        <Form layout="inline">
          <Form.Item label={`${formatMessage(messageIds.common.name)}：`}>
            <Input
              placeholder={formatMessage(
                messageIds.aiManage.userManagment.validate.inputName
              )}
              onChange={(e) => setName(e.target.value)}
              allowClear
            ></Input>
          </Form.Item>
          <Form.Item label={`${formatMessage(messageIds.common.email)}：`}>
            <Input
              placeholder={formatMessage(
                messageIds.aiManage.userManagment.validate.inputEmail
              )}
              onChange={(e) => setEmail(e.target.value)}
              allowClear
            ></Input>
          </Form.Item>
          <Form.Item label={`${formatMessage(messageIds.common.phone)}：`}>
            <Input
              placeholder={formatMessage(
                messageIds.aiManage.userManagment.validate.inputPhone
              )}
              onChange={(e) => setPhone(e.target.value)}
              allowClear
            ></Input>
          </Form.Item>
          <Form.Item>
            <Button type="primary" onClick={onSearch}>
              {formatMessage(messageIds.common.select)}
            </Button>
          </Form.Item>
        </Form>
      </div>

      <div
        css={css`
          margin-top: 30px;
        `}
      >
        <Table
          columns={columns}
          dataSource={dataSource}
          // className="ant-table-thead"
          rowKey={(record) => record?.userId}
          loading={loading}
        ></Table>
      </div>
      <UserCreate
        isModalShow={isModalShow}
        onShow={onCreate}
        fetch={fetch}
      ></UserCreate>
    </div>
  );
});
